// Derive each origin hub's share of a branch from per-origin flow actuals.
//   share(originHub → branch) = Σ flow[origin][branch] / Σ flow[origin][*]
// (i.e. what fraction of an origin hub's sorted volume goes to that branch).
// Shares for a given origin sum to 1 across all branches, so a branch's forecast
// = Σ over origins of (origin hub forecast × share(origin → branch)) conserves
// each hub's volume across the network.

import type { BranchFlow } from "../../features/actuals/branchFlowApi";

const key = (originHubId: string, branchId: string) => `${originHubId}|${branchId}`;

/** Map "originHubId|branchId" → share (0..1). */
export function flowShares(flows: BranchFlow[]): Map<string, number> {
  const pairTotal = new Map<string, number>();
  const originTotal = new Map<string, number>();
  for (const f of flows) {
    pairTotal.set(key(f.originHubId, f.branchId), (pairTotal.get(key(f.originHubId, f.branchId)) ?? 0) + f.volume);
    originTotal.set(f.originHubId, (originTotal.get(f.originHubId) ?? 0) + f.volume);
  }
  const out = new Map<string, number>();
  for (const [k, v] of pairTotal) {
    const origin = k.slice(0, k.indexOf("|"));
    const tot = originTotal.get(origin) ?? 0;
    out.set(k, tot > 0 ? v / tot : 0);
  }
  return out;
}

/** Convenience lookup for a single origin→branch share (0 if none). */
export function shareOf(shares: Map<string, number>, originHubId: string, branchId: string): number {
  return shares.get(key(originHubId, branchId)) ?? 0;
}

/** Network-flow volume for one hub: what originates here, what it ships out to
 * other hubs' branches, what it receives in, and what it actually sorts. */
export interface HubFlowTotals {
  own: number; // volume originating here (the hub's own forecast)
  sentOut: number; // routed out to OTHER hubs' branches (H2H/Direct out)
  receivedIn: number; // routed in from other hubs for THIS hub's branches
  throughput: number; // own − sentOut + receivedIn = volume sorted for this hub's branches
}

/**
 * Per-hub flow totals from each hub's own forecast + the origin→branch shares.
 * `own − sentOut` is the volume a hub keeps for its own branches; `+ receivedIn`
 * adds what other hubs feed it. Conserves volume: Σ throughput = Σ own.
 */
export function hubFlowTotals(
  hubIds: string[],
  branches: { id: string; hubId: string }[],
  ownForecast: Map<string, number>,
  shares: Map<string, number>
): Map<string, HubFlowTotals> {
  const out = new Map<string, HubFlowTotals>();
  for (const h of hubIds) {
    const own = ownForecast.get(h) ?? 0;
    let sentOut = 0;
    let receivedIn = 0;
    for (const b of branches) {
      if (b.hubId !== h) sentOut += own * shareOf(shares, h, b.id); // h → other hub's branch
    }
    for (const o of hubIds) {
      if (o === h) continue;
      const oOwn = ownForecast.get(o) ?? 0;
      for (const b of branches) {
        if (b.hubId === h) receivedIn += oOwn * shareOf(shares, o, b.id); // other hub → h's branch
      }
    }
    out.set(h, { own, sentOut, receivedIn, throughput: own - sentOut + receivedIn });
  }
  return out;
}
